/**
 * Objects Service - Fetches database objects (tables, views, procedures, functions)
 * Supports multiple database engines (MSSQL, PostgreSQL)
 */

import type { DatabaseEngineId } from '@/core/engines';
import { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerListingExecutionId, type ExplorerFetchOrigin } from './explorerQueryUtils';
import { getQueries } from './queries';
import { toKustoObjectRows } from './queries/kustoQueries';
import { mapDatabaseObjectRows } from './databaseObjectRowMapper';

/**
 * C1 (Soporte fixes round 2, Wave C) — same rationale as
 * `databasesService.ts`'s `LISTING_TIMEOUT_MS`: `getTables` drives the Data/
 * Schema Compare setup dialogs' Schema dropdown, and the driver-route query
 * had no per-request bound (pool `requestTimeout: 0`, no caller passed
 * `timeoutMs`) — a wedged connection pended forever. Bounding at 30s here
 * does NOT touch the adapter default, so Query Editor user queries stay
 * unbounded. Duplicated (not shared) per plan guidance — two service files,
 * not worth a new module.
 *
 * Fix round 1 — also applied to `getViews` / `getStoredProcedures` /
 * `getFunctions`: all three are called by `SchemaCompareObjectPicker.tsx`,
 * which renders inside `SchemaCompareSetupDialog.tsx` for single-object
 * compare mode — the same stuck-dropdown surface `getTables` fixes.
 * `getOracleObjects` is NOT bounded — it's reachable only from Object
 * Explorer, never from a compare setup dialog or the picker above.
 */
const LISTING_TIMEOUT_MS = 30_000;

/**
 * C3 (Soporte fixes round 2, Wave C) E2E seam — hold the table listing until
 * the spec releases it, so the compare setup dialogs' "Loading tables…" /
 * "Loading schemas…" label can be asserted deterministically instead of being
 * raced against a fast mock-engine round trip. Mirrors `databasesService`'s
 * `__holdListing` (and the `__forceTablesFailure` seam right below) 1:1: same
 * `window.__SLICE1_DB_SERVICES__` namespace, plain per-connection record,
 * unset in production ⇒ zero behavioural impact. Capped at 10s so a forgotten
 * flag can never wedge the app permanently.
 *
 * Unlike `getDatabases` there is no phase argument: `getTables` has NO
 * separable pre-listing connect step (no pool warm-up — it goes straight to
 * `executeQueryV2`), so the dialogs label it with a single, static
 * "Loading tables…"/"Loading schemas…" rather than inventing a connect phase.
 */
function isTablesHeld(connectionId: string): boolean {
  if (typeof window === 'undefined') return false;
  const holds = (
    window as unknown as {
      __SLICE1_DB_SERVICES__?: { __holdTables?: Record<string, boolean | undefined> };
    }
  ).__SLICE1_DB_SERVICES__?.__holdTables;
  return Boolean(holds?.[connectionId]);
}

async function awaitTablesHold(connectionId: string): Promise<void> {
  if (!isTablesHeld(connectionId)) return;
  const deadline = Date.now() + 10_000;
  while (isTablesHeld(connectionId) && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 25));
  }
}

/**
 * Kusto's listing commands can't `| project` into the `id`/`name` shape
 * `mapDatabaseObjectRows` reads — the Azure Monitor ADX proxy rejects a piped
 * control command with a 400. They name the object column `TableName` or
 * `Name` instead, so reshape here. Every other engine already projects.
 */
function listingRows(
  rows: Record<string, unknown>[],
  engine?: DatabaseEngineId | string
): Record<string, unknown>[] {
  return engine === 'kusto' ? toKustoObjectRows(rows) : rows;
}

/**
 * C2 (Soporte fixes round 2, Wave C) — test-only escape hatch mirroring
 * databasesService.ts's `__forceListingFailure`. The Data/Schema Compare
 * setup dialogs' e2e specs need a deterministic way to force the
 * table-listing call to fail (after a successful database selection) so
 * they can exercise the "inline error + Retry replaces the vanished
 * Schema selector" behavior against the mock engine, which has no
 * built-in way to fail one specific listing query on demand. Set via
 * window.__SLICE1_DB_SERVICES__.__forceTablesFailure[connectionId].
 * Unset in production — zero behavioural impact. Shared by `getTables` and
 * `getSchemas` so one flag fails both listings of a destination cascade.
 */
function forcedTablesFailure(connectionId: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const overrides = (
    window as unknown as {
      __SLICE1_DB_SERVICES__?: {
        __forceTablesFailure?: Record<string, string | undefined>;
      };
    }
  ).__SLICE1_DB_SERVICES__?.__forceTablesFailure;
  return overrides?.[connectionId] || undefined;
}

/**
 * Get only tables for a specific database
 * @param connectionId The connection ID
 * @param database The database name
 * @param engine Database engine type (defaults to 'mssql')
 * @param origin `'preload'` when issued by the Object Explorer's background
 *   preload — its execution id is then the gated `explorer_preload` site on
 *   the SQL Tools route (1.4.21 review F3); every other caller is `'user'`.
 */
export async function getTables(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
  origin?: ExplorerFetchOrigin
): Promise<{ tables: DatabaseObject[]; error?: string }> {
  const queries = getQueries(engine);

  const forced = forcedTablesFailure(connectionId);
  if (forced) {
    return { tables: [], error: forced };
  }

  try {
    // C3 E2E seam (no-op unless a spec set `__holdTables`).
    await awaitTablesHold(connectionId);

    const result = await executeQueryV2({
      connectionId,
      sql: queries.tables,
      database,
      executionId: createExplorerListingExecutionId(connectionId, origin),
      options: { timeoutMs: LISTING_TIMEOUT_MS },
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[ObjectsService] Error getting tables: ${errorMsg}`);
      return { tables: [], error: errorMsg };
    }

    const rows = listingRows(result.resultSets?.[0]?.rows || [], engine);
    const tables: DatabaseObject[] = mapDatabaseObjectRows({
      rows,
      objectType: 'table',
      connectionId,
      database,
    });

    return { tables };
  } catch (error) {
    console.error('[ObjectsService] Error in getTables:', error);
    return {
      tables: [],
      error: error instanceof Error ? error.message : 'Unknown error getting tables'
    };
  }
}

/**
 * Schemas a table can be created in for `database`, read from the engine's
 * catalog (`queries.schemas`) rather than derived from the table listing. A
 * database with no tables yet still has `dbo` / `public` / its Oracle user,
 * and Data Import's Target step must be able to pick one before the first
 * table exists — deriving schemas from `getTables` left that dropdown empty
 * and the wizard stuck on "Choose a schema to continue.". Same 30s bound and
 * `__forceTablesFailure` / `__holdTables` e2e seams as `getTables`.
 */
export async function getSchemas(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
): Promise<{ schemas: string[]; error?: string }> {
  const queries = getQueries(engine);

  const forced = forcedTablesFailure(connectionId);
  if (forced) {
    return { schemas: [], error: forced };
  }

  try {
    await awaitTablesHold(connectionId);

    const result = await executeQueryV2({
      connectionId,
      sql: queries.schemas,
      database,
      executionId: createExplorerListingExecutionId(connectionId),
      options: { timeoutMs: LISTING_TIMEOUT_MS },
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[ObjectsService] Error getting schemas: ${errorMsg}`);
      return { schemas: [], error: errorMsg };
    }

    const rows = listingRows(result.resultSets?.[0]?.rows || [], engine);
    const schemas = Array.from(
      new Set(rows.map((row) => row.name).filter((name): name is string => typeof name === 'string' && name.length > 0)),
    );
    return { schemas };
  } catch (error) {
    console.error('[ObjectsService] Error in getSchemas:', error);
    return {
      schemas: [],
      error: error instanceof Error ? error.message : 'Unknown error getting schemas',
    };
  }
}

/**
 * Get only views for a specific database
 * @param connectionId The connection ID
 * @param database The database name
 * @param engine Database engine type (defaults to 'mssql')
 */
export async function getViews(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
  origin?: ExplorerFetchOrigin
): Promise<{ views: DatabaseObject[]; error?: string }> {
  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.views,
      database,
      executionId: createExplorerListingExecutionId(connectionId, origin),
      options: { timeoutMs: LISTING_TIMEOUT_MS },
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[ObjectsService] Error getting views: ${errorMsg}`);
      return { views: [], error: errorMsg };
    }

    const rows = listingRows(result.resultSets?.[0]?.rows || [], engine);
    const views: DatabaseObject[] = mapDatabaseObjectRows({
      rows,
      objectType: 'view',
      connectionId,
      database,
    });

    return { views };
  } catch (error) {
    console.error('[ObjectsService] Error in getViews:', error);
    return {
      views: [],
      error: error instanceof Error ? error.message : 'Unknown error getting views'
    };
  }
}

/**
 * Get only stored procedures for a specific database
 * @param connectionId The connection ID
 * @param database The database name
 * @param engine Database engine type (defaults to 'mssql')
 */
export async function getStoredProcedures(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
  origin?: ExplorerFetchOrigin
): Promise<{ procedures: DatabaseObject[]; error?: string }> {
  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.storedProcedures,
      database,
      executionId: createExplorerListingExecutionId(connectionId, origin),
      options: { timeoutMs: LISTING_TIMEOUT_MS },
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[ObjectsService] Error getting stored procedures: ${errorMsg}`);
      return { procedures: [], error: errorMsg };
    }

    const rows = listingRows(result.resultSets?.[0]?.rows || [], engine);
    const procedures: DatabaseObject[] = mapDatabaseObjectRows({
      rows,
      objectType: 'stored-procedure',
      connectionId,
      database,
    });

    return { procedures };
  } catch (error) {
    console.error('[ObjectsService] Error in getStoredProcedures:', error);
    return {
      procedures: [],
      error: error instanceof Error ? error.message : 'Unknown error getting stored procedures'
    };
  }
}

/**
 * Get only functions for a specific database
 * @param connectionId The connection ID
 * @param database The database name
 * @param engine Database engine type (defaults to 'mssql')
 */
export async function getFunctions(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
  origin?: ExplorerFetchOrigin
): Promise<{ functions: DatabaseObject[]; error?: string }> {
  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.functions,
      database,
      executionId: createExplorerListingExecutionId(connectionId, origin),
      options: { timeoutMs: LISTING_TIMEOUT_MS },
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[ObjectsService] Error getting functions: ${errorMsg}`);
      return { functions: [], error: errorMsg };
    }

    const rows = listingRows(result.resultSets?.[0]?.rows || [], engine);
    const functions: DatabaseObject[] = mapDatabaseObjectRows({
      rows,
      objectType: 'function',
      connectionId,
      database,
    });

    return { functions };
  } catch (error) {
    console.error('[ObjectsService] Error in getFunctions:', error);
    return {
      functions: [],
      error: error instanceof Error ? error.message : 'Unknown error getting functions'
    };
  }
}

/**
 * Object type to query key mapping for engine-specific object types that
 * aren't in the standard tables/views/procedures/functions set. Originally
 * Oracle-only (packages, sequences, ...); kusto's materialized views and
 * external tables — and SQL Server's three user-defined-type kinds — reuse the
 * same generic `getOracleObjects` lookup below.
 */
const oracleObjectQueryMap: Record<string, string> = {
  'package': 'packages',
  'sequence': 'sequences',
  'synonym': 'synonyms',
  'db-link': 'dbLinks',
  'materialized-view': 'materializedViews',
  'type': 'types',
  'directory': 'directories',
  'external-table': 'externalTables',
  'property-graph': 'propertyGraphs',
  // SQL Server user-defined types (Programmability -> Types).
  'table-type': 'tableTypes',
  'alias-type': 'aliasTypes',
  'clr-type': 'clrTypes',
};

/**
 * Get Oracle-specific objects (packages, sequences, synonyms, etc.)
 */
export async function getOracleObjects(
  connectionId: string,
  database: string,
  objectType: DatabaseObjectType,
  engine?: DatabaseEngineId | string,
  origin?: ExplorerFetchOrigin
): Promise<{ objects: DatabaseObject[]; error?: string }> {
  const queryKey = oracleObjectQueryMap[objectType];
  if (!queryKey) {
    return { objects: [], error: `Unsupported Oracle object type: ${objectType}` };
  }

  const queries = getQueries(engine) as unknown as Record<string, string>;
  const sql = queries[queryKey];
  if (!sql || typeof sql !== 'string') {
    return { objects: [] };
  }

  try {
    const result = await executeQueryV2({
      connectionId,
      sql,
      database,
      executionId: createExplorerListingExecutionId(connectionId, origin),
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[ObjectsService] Error getting ${objectType}s: ${errorMsg}`);
      return { objects: [], error: errorMsg };
    }

    const rows = listingRows(result.resultSets?.[0]?.rows || [], engine);
    const objects: DatabaseObject[] = mapDatabaseObjectRows({
      rows,
      objectType,
      connectionId,
      database,
    });

    return { objects };
  } catch (error) {
    console.error(`[ObjectsService] Error in getOracleObjects (${objectType}):`, error);
    return {
      objects: [],
      error: error instanceof Error ? error.message : `Unknown error getting ${objectType}s`
    };
  }
}
